import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { MatLegacyMenuTrigger } from '@angular/material/legacy-menu';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, map, of } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { FetcherDataSource } from '../table/data-sources/fetcher.data-source';

interface PaginatorContext {
    pagesCount: number;
    pageIndex: number;
    length: number;
    pageSize: number;
}

@Component({
    selector: 'app-paginator',
    templateUrl: './paginator.component.html',
    styleUrls: ['./paginator.component.scss'],
})
export class AppPaginatorComponent {
    private _dataSource$ = new BehaviorSubject<FetcherDataSource>(undefined);
    private _length$ = new BehaviorSubject<number>(undefined);
    private _pageSize$ = new BehaviorSubject<number>(undefined);
    private _pageIndex$ = new BehaviorSubject<number>(undefined);

    @Input() set pageIndex(value: number) {
        this._pageIndex$.next(value);
    }

    @Input() set pageSize(value: number) {
        this._pageSize$.next(value);
    }

    @Input() set length(value: number) {
        this._length$.next(value);
    }

    @Input() set dataSource(value: FetcherDataSource) {
        this._dataSource$.next(value);
    }

    @Input() pageSizeOptions: number[] = [10, 20, 50, 100];

    @Output() page: EventEmitter<any> = new EventEmitter();
    @Output() pageSizeChange: EventEmitter<number> = new EventEmitter();

    protected readonly context$: Observable<PaginatorContext> = combineLatest({
        pagination: this._dataSource$.pipe(
            switchMap(dataSource => dataSource?.paginator$ ?? of(null)),
        ),
        length: this._length$,
        pageSize: this._pageSize$,
        pageIndex: this._pageIndex$,
    }).pipe(
        map(({ pagination, length, pageSize, pageIndex }) => {
            if (pagination) {
                return {
                    length: pagination.length,
                    pageSize: pagination.pageSize,
                    pageIndex: pagination.page,
                    pagesCount: Math.ceil(pagination.length / pagination.pageSize),
                };
            }

            return {
                length,
                pageSize,
                pageIndex,
                pagesCount: Math.ceil(length / pageSize),
            };
        }),
        shareReplay(1),
    );

    protected readonly pageOptions$: Observable<{
        index: number;
        label: string;
    }[]> = this.context$.pipe(
        map(context => {
            if (context.pagesCount > 10) {
                // Show the first and last pages, 5 pages around the current page, and every 10 pages otherwise
                const pages = Array.from({ length: context.pagesCount }, (_, i) => i);
                const first = pages.slice(0, 1);
                const last = pages.slice(-1);
                // const middle = pages.slice(5, -5).filter(page => page % 10 === 0);

                // 5 pages either side of the current page
                const start = Math.max(0, context.pageIndex - 5);
                const end = Math.min(context.pagesCount, context.pageIndex + 5);
                const around = pages.slice(start, end);

                const numbers = Array.from(new Set([
                    ...first,
                    // ...middle,
                    ...around,
                    ...last,
                ]));

                return numbers
                    .sort((a, b) => a - b)
                    .map(index => {
                        // Check if current
                        if (index === context.pageIndex) {
                            return ({
                                index,
                                label: `${index + 1} (current)`,
                            });
                        }

                        // Check if last
                        if (index === context.pagesCount - 1) {
                            return ({
                                index,
                                label: `${index + 1} (last)`,
                            });
                        }

                        return ({
                            index,
                            label: `${index + 1} `,
                        });
                    });
            }

            return Array.from({ length: context.pagesCount }, (_, i) => ({
                index: i,
                label: `${i + 1}`,
            }));
        }),
        shareReplay(1),
    );

    setPage(pageIndex: number) {
        this._dataSource$.value?.paginate({
            ...this._dataSource$.value?.paginator,
            pageIndex,
        });

        this.page.emit(pageIndex);
    }

    changePageSize(size: number) {
        this._dataSource$.value?.changePageSize(size);

        this.pageSizeChange.emit(size);
    }

    _onGoToKeyUp($event: KeyboardEvent, menu: MatLegacyMenuTrigger): void {
        if ($event.key === 'Enter') {
            const input$ = $event.target as HTMLInputElement;

            this.setPage(Number(input$.value) - 1);
            input$.value = '';

            menu.closeMenu();
        }
    }

    goToPage(input: HTMLInputElement, menu: MatLegacyMenuTrigger): void {
        this.setPage(Number(input.value) - 1);
        input.value = '';

        menu.closeMenu();
    }
}
